import { useMemo, useState } from "react";
import {
  BUDGET_OPTIONS,
  INTAKE_PRIVACY_NOTE,
  MOVE_IN_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  ROOM_TYPE_OPTIONS,
  type PropertyTypeId,
} from "@/config/intake";
import { useContact } from "@/context/ContactContext";
import { IDENTITY_CARDS } from "@/data/identities";
import {
  resolveRegionOptions,
  submitLead,
  validateIntakeForm,
  type IntakeFormData,
} from "@/lib/intake";
import type { IntakeContext } from "@/types/intake";
import {
  INTAKE_SCHOOL_IDS,
  isIntakeSchoolOther,
  SCHOOL_LABELS,
  type IntakeSchoolId,
} from "@/types/navigation";

type IntakeFormProps = {
  ctx: IntakeContext;
};

function emptyForm(ctx: IntakeContext): IntakeFormData {
  return {
    displayName: "",
    phone: "",
    school: ctx.school ?? "",
    schoolName: "",
    identity: ctx.identity ?? "",
    regions: [],
    budget: "",
    roomTypes: "",
    propertyTypes: [],
    moveIn: "",
    note: "",
    criterion: ctx.criterion,
    communityId: ctx.communityId,
    communityNameEn: ctx.communityNameEn,
    unitId: ctx.unitId,
    source: ctx.source,
  };
}

function Chip({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-pill border px-3.5 py-2 text-[13px] font-medium transition ${
        selected
          ? "border-coral-700 bg-coral-50 text-coral-900"
          : "border-ink-200 bg-paper text-ink-700"
      }`}
    >
      {label}
    </button>
  );
}

export function IntakeForm({ ctx }: IntakeFormProps) {
  const { completeIntake } = useContact();
  const [form, setForm] = useState<IntakeFormData>(() => emptyForm(ctx));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const showSchoolPicker = !ctx.school;
  const showIdentityPicker = !ctx.identity;
  const isOtherSchool = isIntakeSchoolOther(form.school);
  const showRegionPicker = Boolean(form.school) && !isOtherSchool;

  const regionOptions = useMemo(
    () => resolveRegionOptions(form.school, form.identity, ctx.regions),
    [form.school, form.identity, ctx.regions],
  );

  const toggleRegion = (id: string) => {
    setForm((f) => ({
      ...f,
      regions: f.regions.includes(id) ? f.regions.filter((r) => r !== id) : [...f.regions, id],
    }));
  };

  const toggleProperty = (id: PropertyTypeId) => {
    setForm((f) => {
      if (id === "any") return { ...f, propertyTypes: ["any"] };
      const next = f.propertyTypes.filter((p) => p !== "any");
      return {
        ...f,
        propertyTypes: next.includes(id) ? next.filter((p) => p !== id) : [...next, id],
      };
    });
  };

  const pickSchool = (id: IntakeSchoolId) => {
    setForm((f) => ({
      ...f,
      school: id,
      schoolName: id === "other" ? f.schoolName : "",
      regions: [],
    }));
  };

  const pickIdentity = (id: (typeof IDENTITY_CARDS)[number]["id"]) => {
    setForm((f) => ({ ...f, identity: id, regions: [] }));
  };

  const handleSubmit = async () => {
    setError(null);
    const result = validateIntakeForm(form);
    if (!result.ok) {
      setError(result.message);
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitLead(result.data, regionOptions);
      completeIntake(res.summary);
    } catch (e) {
      setError(e instanceof Error ? e.message : "提交失败，请稍后再试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-h-[min(70vh,520px)] overflow-y-auto overscroll-contain pr-1">
      <Field label="怎么称呼你" required>
        <input
          type="text"
          value={form.displayName}
          maxLength={20}
          placeholder="例如：小陈、陈同学"
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          className="w-full rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5 text-[13px] text-ink-900 placeholder:text-ink-400"
        />
      </Field>

      <Field label="手机号" required>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={form.phone}
          maxLength={20}
          placeholder="例如：+65 8123 4567 或 13800138000"
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          className="w-full rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5 text-[13px] text-ink-900 placeholder:text-ink-400"
        />
      </Field>

      {showSchoolPicker ? (
        <Field label="学校" required>
          <div className="flex flex-wrap gap-2">
            {INTAKE_SCHOOL_IDS.map((id) => (
              <Chip
                key={id}
                label={id === "other" ? "其他" : SCHOOL_LABELS[id]}
                selected={form.school === id}
                onClick={() => pickSchool(id)}
              />
            ))}
          </div>
          {isOtherSchool ? (
            <input
              type="text"
              value={form.schoolName}
              maxLength={64}
              placeholder="请填写学校名称"
              onChange={(e) => setForm((f) => ({ ...f, schoolName: e.target.value }))}
              className="mt-2.5 w-full rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5 text-[13px] text-ink-900 placeholder:text-ink-400"
            />
          ) : null}
        </Field>
      ) : (
        <div className="mb-4 rounded-xl border border-[var(--line-soft)] bg-surface-50 px-3.5 py-2.5 text-[13px] text-ink-700">
          <span className="text-ink-500">学校 </span>
          {SCHOOL_LABELS[form.school as keyof typeof SCHOOL_LABELS]}
        </div>
      )}

      {showIdentityPicker ? (
        <Field label="身份" required>
          <div className="flex flex-col gap-2">
            {IDENTITY_CARDS.map((card) => (
              <button
                key={card.id}
                type="button"
                onClick={() => pickIdentity(card.id)}
                className={`rounded-xl border px-3.5 py-2.5 text-left text-[13px] transition ${
                  form.identity === card.id
                    ? "border-coral-700 bg-coral-50 text-coral-900"
                    : "border-ink-200 bg-paper text-ink-700"
                }`}
              >
                {card.title}
              </button>
            ))}
          </div>
        </Field>
      ) : form.identity ? (
        <div className="mb-4 rounded-xl border border-[var(--line-soft)] bg-surface-50 px-3.5 py-2.5 text-[13px] text-ink-700">
          <span className="text-ink-500">身份 </span>
          {IDENTITY_CARDS.find((c) => c.id === form.identity)?.title}
        </div>
      ) : null}

      {showRegionPicker ? (
        <Field label="意向片区" required>
          {regionOptions.length === 0 ? (
            <p className="text-[12.5px] text-ink-500">请先选择学校与身份</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {regionOptions.map((r) => (
                <Chip
                  key={r.id}
                  label={r.nameCn}
                  selected={form.regions.includes(r.id)}
                  onClick={() => toggleRegion(r.id)}
                />
              ))}
            </div>
          )}
        </Field>
      ) : null}

      <Field label="月预算" required>
        <div className="flex flex-wrap gap-2">
          {BUDGET_OPTIONS.map((o) => (
            <Chip
              key={o.id}
              label={o.label}
              selected={form.budget === o.id}
              onClick={() => setForm((f) => ({ ...f, budget: o.id }))}
            />
          ))}
        </div>
      </Field>

      <Field label="户型" required>
        <div className="flex flex-wrap gap-2">
          {ROOM_TYPE_OPTIONS.map((o) => (
            <Chip
              key={o.id}
              label={o.label}
              selected={form.roomTypes === o.id}
              onClick={() => setForm((f) => ({ ...f, roomTypes: o.id }))}
            />
          ))}
        </div>
      </Field>

      <Field label="房源类型" required>
        <div className="flex flex-wrap gap-2">
          {PROPERTY_TYPE_OPTIONS.map((o) => (
            <Chip
              key={o.id}
              label={o.label}
              selected={form.propertyTypes.includes(o.id)}
              onClick={() => toggleProperty(o.id)}
            />
          ))}
        </div>
      </Field>

      <Field label="入住时间" required>
        <div className="flex flex-wrap gap-2">
          {MOVE_IN_OPTIONS.map((o) => (
            <Chip
              key={o.id}
              label={o.label}
              selected={form.moveIn === o.id}
              onClick={() => setForm((f) => ({ ...f, moveIn: o.id }))}
            />
          ))}
        </div>
      </Field>

      <Field label="补充说明">
        <input
          type="text"
          value={form.note}
          maxLength={200}
          placeholder="室友、宠物、楼层偏好等"
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          className="w-full rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5 text-[13px] text-ink-900 placeholder:text-ink-400"
        />
      </Field>

      <p className="mt-2 text-[11px] leading-relaxed text-ink-500">{INTAKE_PRIVACY_NOTE}</p>

      {error ? (
        <p className="mt-3 text-[13px] text-coral-700" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={submitting}
        onClick={handleSubmit}
        className="btn-primary mt-5 w-full disabled:opacity-60"
      >
        {submitting ? "提交中…" : "提交并添加顾问"}
      </button>

      <p className="mt-2 text-center text-[11px] text-ink-400">
        提交后将展示顾问企微二维码
      </p>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <p className="mb-2 text-[13px] font-semibold text-ink-900">
        {label}
        {required ? <span className="text-coral-700"> *</span> : null}
      </p>
      {children}
    </div>
  );
}
